// Global cmd-K palette.
//
// Two modes in one shell:
//  1. COMMANDS — shown on an empty query and on a ">" prefix (VS Code style).
//     A static registry of workspace actions (Compose, New event, Go to <app>,
//     Toggle theme, …). Each command navigates and/or fires a namespaced
//     CustomEvent that the owning route listens for (see integration manifest).
//  2. SEARCH — any other plain-text query hits the federated /api/search below.
//
// Both modes share the same keyboard model (↑↓ to move, ↵ to run/open, esc to
// close). Rendered as Proton's Commander modal.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, type NavigateFunction } from "react-router-dom";
import {
  Mail, Users, Files as FilesIcon, Calendar as CalendarIcon, Search,
  PenSquare, CalendarPlus, UserPlus, Upload, Video, CheckCheck, SunMoon,
  Contact, Settings, Home, Command as CommandIcon,
} from "lucide-react";
import clsx from "clsx";
import { http } from "@/api";
import { Modal } from "@/proton/ui/Modal";
import { Button } from "@/proton/ui/Button";
import { Icon } from "@/proton/ui/Icon";
import { runCommand as runAppCommand } from "@/lib/commands";

interface SearchHit {
  type: "mail" | "ticket" | "file" | "event" | "contact";
  id: string;
  title: string;
  snippet?: string;
  href: string;
  meta?: string;
  ts?: number;
}

const TYPE_META: Record<SearchHit["type"], { label: string; icon: typeof Mail }> = {
  mail:   { label: "Mail",     icon: Mail },
  ticket: { label: "Ticket",   icon: Users },
  file:   { label: "File",     icon: FilesIcon },
  event:  { label: "Event",    icon: CalendarIcon },
  contact:{ label: "People",   icon: Contact },
};

// ─── Command registry ────────────────────────────────────────────────────────

interface Command {
  id: string;
  label: string;
  hint?: string;
  keywords?: string;               // extra match terms not shown in the label
  icon: typeof Mail;
  section: "Actions" | "Go to";
  run: (nav: NavigateFunction) => void;
}

// Toggle a light theme class + persist. The product is dark-only today, so this
// is intentionally a soft no-op (the class has no styles bound); it exists so
// the command surface is complete and future-proof.
function toggleTheme() {
  const el = document.documentElement;
  const isLight = el.classList.toggle("theme-light");
  try { localStorage.setItem("l7-theme", isLight ? "light" : "dark"); } catch { /* private mode */ }
}

const COMMANDS: Command[] = [
  {
    id: "compose", label: "Compose", hint: "New message", keywords: "mail write email new",
    icon: PenSquare, section: "Actions",
    run: (nav) => { nav("/mail"); runAppCommand("compose"); },
  },
  {
    id: "new-event", label: "New event", hint: "Add to calendar", keywords: "calendar meeting schedule",
    icon: CalendarPlus, section: "Actions",
    run: (nav) => { nav("/calendar"); runAppCommand("new-event"); },
  },
  {
    id: "new-contact", label: "New contact", hint: "Add a person", keywords: "contacts person address book",
    icon: UserPlus, section: "Actions",
    run: (nav) => { nav("/contacts"); runAppCommand("new-contact"); },
  },
  {
    id: "upload-file", label: "Upload file", hint: "Add to Files", keywords: "files drive attachment upload",
    icon: Upload, section: "Actions",
    run: (nav) => { nav("/files"); runAppCommand("upload-file"); },
  },
  {
    id: "start-meeting", label: "Start meeting", hint: "Instant video call", keywords: "meet video call room",
    icon: Video, section: "Actions",
    run: (nav) => { nav("/meetings"); runAppCommand("start-meeting"); },
  },
  {
    id: "mark-all-read", label: "Mark all read", hint: "Clear unread in this mailbox", keywords: "mail inbox unread seen",
    icon: CheckCheck, section: "Actions",
    run: (nav) => { nav("/mail"); runAppCommand("mark-all-read"); },
  },
  {
    id: "toggle-theme", label: "Toggle theme", hint: "Light / dark", keywords: "dark light appearance",
    icon: SunMoon, section: "Actions",
    run: () => { toggleTheme(); },
  },
  // Go to <app>
  { id: "go-home",     label: "Go to Home",     keywords: "dashboard overview", icon: Home, section: "Go to", run: (n) => n("/home") },
  { id: "go-mail",     label: "Go to Mail",     keywords: "inbox email",        icon: Mail, section: "Go to", run: (n) => n("/mail") },
  { id: "go-support",  label: "Go to Support",  keywords: "tickets helpdesk",   icon: Users, section: "Go to", run: (n) => n("/support") },
  { id: "go-files",    label: "Go to Files",    keywords: "drive documents",    icon: FilesIcon, section: "Go to", run: (n) => n("/files") },
  { id: "go-calendar", label: "Go to Calendar", keywords: "events schedule",    icon: CalendarIcon, section: "Go to", run: (n) => n("/calendar") },
  { id: "go-meetings", label: "Go to Meetings", keywords: "video calls",        icon: Video, section: "Go to", run: (n) => n("/meetings") },
  { id: "go-contacts", label: "Go to Contacts", keywords: "people address",     icon: Contact, section: "Go to", run: (n) => n("/contacts") },
  { id: "go-settings", label: "Go to Settings", keywords: "preferences config", icon: Settings, section: "Go to", run: (n) => n("/settings") },
];

const SECTION_ORDER: Command["section"][] = ["Actions", "Go to"];

// Rank: exact/prefix label matches before mid-string, and label matches before
// keyword-only matches. Empty query returns the full registry in author order.
function filterCommands(query: string): Command[] {
  const t = query.trim().toLowerCase();
  if (!t) return COMMANDS;
  const scored: Array<{ c: Command; score: number }> = [];
  for (const c of COMMANDS) {
    const label = c.label.toLowerCase();
    const hay = `${label} ${(c.hint ?? "").toLowerCase()} ${(c.keywords ?? "").toLowerCase()}`;
    let score = -1;
    if (label.startsWith(t)) score = 3;
    else if (label.includes(t)) score = 2;
    else if (hay.includes(t)) score = 1;
    if (score >= 0) scored.push({ c, score });
  }
  return scored
    .sort((a, b) => b.score - a.score)
    .map((s) => s.c);
}

export function CmdKPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function onOpen() { setOpen(true); }
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    }
    window.addEventListener("__cmdk_open", onOpen as EventListener);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("__cmdk_open", onOpen as EventListener);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
      setSelected(0);
    } else {
      setQ("");
      setHits([]);
    }
  }, [open]);

  // Reset the highlight whenever the query changes so the first row of the
  // freshly-filtered list is always the default target.
  useEffect(() => { setSelected(0); }, [q]);

  // Command mode: empty query, or an explicit ">" prefix. Anything else is a
  // federated search term.
  const trimmed = q.trim();
  const inCommandMode = trimmed === "" || trimmed.startsWith(">");
  const commandQuery = trimmed.startsWith(">") ? trimmed.slice(1).trim() : "";
  const commands = useMemo(
    () => (inCommandMode ? filterCommands(commandQuery) : []),
    [inCommandMode, commandQuery],
  );

  // Debounced search with race protection: each keystroke aborts the previous
  // in-flight request so a slow earlier response can't overwrite a newer one.
  // Skipped entirely in command mode (empty or ">"-prefixed queries).
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length === 0 || term.startsWith(">")) {
      setHits([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const ctrl = new AbortController();
    const handle = setTimeout(async () => {
      try {
        const res = await http<{ hits: SearchHit[] }>(
          `/api/search?q=${encodeURIComponent(term)}`,
          { signal: ctrl.signal },
        );
        if (ctrl.signal.aborted) return;
        setHits(res.hits ?? []);
        setSelected(0);
      } catch (err) {
        if ((err as Error)?.name === "AbortError") return;
        setHits([]);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, 180);
    return () => {
      clearTimeout(handle);
      ctrl.abort();
    };
  }, [q, open]);

  const grouped = useMemo(() => {
    const out: Record<SearchHit["type"], SearchHit[]> = { mail: [], ticket: [], file: [], event: [], contact: [] };
    // Defensive: an unknown type from a newer server must not throw here and
    // take the whole palette down.
    for (const h of hits) (out[h.type] ??= []).push(h);
    return out;
  }, [hits]);

  // Commands grouped by section, preserving the flat order used for selection.
  const commandSections = useMemo(() => {
    return SECTION_ORDER
      .map((section) => ({ section, list: commands.filter((c) => c.section === section) }))
      .filter((g) => g.list.length > 0);
  }, [commands]);

  // Selection indexes into these, NOT into `hits` / `commands`.
  //
  // Rows render grouped (hits by type, commands by section) while `selected` is
  // a flat counter assigned in render order. Reading the answer back out of the
  // unsorted source arrays meant that as soon as the API returned interleaved
  // types, Enter opened a different item than the one highlighted. Flattening
  // in the exact order the rows are emitted keeps the two in agreement.
  const orderedHits = useMemo(
    () => (Object.keys(grouped) as SearchHit["type"][]).flatMap((type) => grouped[type]),
    [grouped],
  );
  const orderedCommands = useMemo(
    () => commandSections.flatMap((g) => g.list),
    [commandSections],
  );

  // Total navigable rows in the currently-visible list.
  const navLen = inCommandMode ? orderedCommands.length : orderedHits.length;

  function go(h: SearchHit) {
    setOpen(false);
    navigate(h.href);
  }

  function runCommand(c: Command) {
    setOpen(false);
    c.run(navigate);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected((s) => Math.min(s + 1, Math.max(navLen - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected((s) => Math.max(s - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (inCommandMode) {
        if (orderedCommands[selected]) runCommand(orderedCommands[selected]);
      } else if (orderedHits[selected]) {
        go(orderedHits[selected]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  if (!open) return null;

  let cmdIdx = -1;   // flat index across command sections
  let hitIdx = -1;   // flat index across hit groups
  // Proton Commander (components/commander): small modal, unstyled search field
  // with a Clear button, results as dropdown items (icon, label, detail).
  return (
    <Modal size="small" dialogClassName="commander-modal" onClose={() => setOpen(false)} labelledBy="commander-label">
      <div className="flex flex-col flex-nowrap min-h-0">
        <div className="commander-search-wrapper">
          <label id="commander-label" htmlFor="commander-search-input" className="sr-only">
            Search your workspace, or type &gt; for commands
          </label>
          <div className="commander-search">
            <Icon as={inCommandMode ? CommandIcon : Search} size={4.5} />
            <input
              id="commander-search-input"
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search, or type > for commands"
              autoComplete="off"
              className="commander-search-input"
            />
            {!inCommandMode && loading && <span className="circle-loader color-weak" aria-label="Searching" />}
            <Button shape="ghost" color="weak" size="small" disabled={q.length === 0} onClick={() => { setQ(""); inputRef.current?.focus(); }}>
              Clear
            </Button>
          </div>
        </div>
        <span className="sr-only" aria-live="polite" aria-atomic="true">
          {inCommandMode ? `${commands.length} commands` : loading ? "Searching" : `${hits.length} results`}
        </span>

        <div className="commander-results-wrapper">
          {/* COMMAND MODE */}
          {inCommandMode && commandSections.length === 0 && (
            <p className="m-0 px-3 py-6 text-center color-weak">No command matches “{commandQuery}”.</p>
          )}
          {inCommandMode && commandSections.map(({ section, list }) => (
            <ul key={section} className="m-0 p-0 list-none">
              <li className="dropdown-item-title" role="presentation">{section}</li>
              {list.map((c) => {
                cmdIdx += 1;
                const myIdx = cmdIdx;
                return (
                  <li key={c.id} className="dropdown-item">
                    <button
                      type="button"
                      className={clsx("dropdown-item-button", myIdx === selected && "dropdown-item--is-selected")}
                      aria-current={myIdx === selected}
                      onMouseEnter={() => setSelected(myIdx)}
                      onClick={() => runCommand(c)}
                    >
                      <Icon as={c.icon} className="mr-2" />
                      <span className="flex-1 min-w-0 text-ellipsis">{markMatch(c.label, commandQuery)}</span>
                      {c.hint && <span className="text-sm color-weak shrink-0">{c.hint}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          ))}

          {/* SEARCH MODE */}
          {!inCommandMode && !loading && hits.length === 0 && (
            <p className="m-0 px-3 py-6 text-center color-weak">No results for “{trimmed}”.</p>
          )}
          {!inCommandMode && (Object.keys(grouped) as SearchHit["type"][]).map((type) => {
            const list = grouped[type];
            if (list.length === 0) return null;
            const meta = TYPE_META[type];
            return (
              <ul key={type} className="m-0 p-0 list-none">
                <li className="dropdown-item-title" role="presentation">{meta.label} · {list.length}</li>
                {list.map((h) => {
                  // Capture per-row index in a const before the callback closes
                  // over it — a shared mutable would snap selection to the last
                  // row on any hover.
                  hitIdx += 1;
                  const myIdx = hitIdx;
                  return (
                    <li key={`${h.type}:${h.id}`} className="dropdown-item">
                      <button
                        type="button"
                        className={clsx("dropdown-item-button", myIdx === selected && "dropdown-item--is-selected")}
                        aria-current={myIdx === selected}
                        onMouseEnter={() => setSelected(myIdx)}
                        onClick={() => go(h)}
                      >
                        <Icon as={meta.icon} className="mr-2" />
                        <span className="flex-1 min-w-0">
                          <span className="block text-ellipsis">{markMatch(h.title, trimmed)}</span>
                          {h.snippet && <span className="block text-ellipsis text-sm color-weak">{h.snippet}</span>}
                        </span>
                        {h.meta && <span className="text-sm color-weak shrink-0">{h.meta}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}

// Proton's <Mark>: embolden the run that matched the query.
function markMatch(text: string, query: string): React.ReactNode {
  const q = query.trim();
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="commander-mark">{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}
